// Execution Validation Service
// Real-time validation during task execution (timing, sequence, completeness, quality)

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

export class ExecutionValidationService {
  constructor() {
    this.rulesCollection = collection(db, 'validation_rules');
  }
  
  /**
   * Validate action during execution
   */
  async validateAction(taskExecution, actionData) {
    try {
      // Get validation rules for this action
      const rules = await this.getValidationRules(
        taskExecution.protocol_id,
        actionData.action_id,
        actionData.step_id
      );
      
      const results = [];
      
      for (const rule of rules) {
        const result = await this.executeRule(rule, taskExecution, actionData);
        results.push(result);
        
        // If blocking rule fails, throw error
        if (!result.passed && rule.blocking) {
          throw new ValidationError(rule.validation_message.failure || 'Validation failed');
        }
      }
      
      return {
        passed: results.every(r => r.passed),
        results: results,
        warnings: results.filter(r => !r.passed && r.severity === 'warning')
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }
      console.error('Error validating action:', error);
      throw error;
    }
  }
  
  /**
   * Execute validation rule
   */
  async executeRule(rule, taskExecution, actionData) {
    switch (rule.rule_type) {
      case 'timing':
        return this.validateTiming(rule, actionData);
      case 'sequence':
        return this.validateSequence(rule, taskExecution, actionData);
      case 'completeness':
        return this.validateCompleteness(rule, actionData);
      case 'quality':
        return this.validateQuality(rule, actionData);
      default:
        return {
          passed: true,
          severity: 'info',
          message: 'Unknown rule type'
        };
    }
  }
  
  /**
   * Validate timing
   */
  validateTiming(rule, actionData) {
    const duration = actionData.duration_seconds || 0;
    const minDuration = rule.rule_definition.min_duration_seconds || 0;
    const maxDuration = rule.rule_definition.max_duration_seconds || Infinity;
    
    if (minDuration > 0 && duration < minDuration) {
      return {
        passed: false,
        severity: rule.severity || 'warning',
        message: `Action completed too quickly. Minimum ${minDuration}s required.`,
        rule_name: rule.rule_name
      };
    }
    
    if (maxDuration < Infinity && duration > maxDuration) {
      return {
        passed: false,
        severity: rule.severity || 'warning',
        message: `Action took too long. Maximum ${maxDuration}s allowed.`,
        rule_name: rule.rule_name
      };
    }
    
    return {
      passed: true,
      severity: 'info',
      message: rule.validation_message.success || 'Timing validation passed'
    };
  }
  
  /**
   * Validate sequence
   */
  validateSequence(rule, taskExecution, actionData) {
    const requiredPrerequisites = rule.rule_definition.required_prerequisites || [];
    const stepsCompleted = taskExecution.steps_completed || [];
    
    // Check if all prerequisites are completed
    for (const prerequisiteId of requiredPrerequisites) {
      const prerequisiteCompleted = stepsCompleted.some(step => 
        step.step_id === prerequisiteId && step.validation_passed
      );
      
      if (!prerequisiteCompleted) {
        return {
          passed: false,
          severity: rule.severity || 'error',
          message: `Required prerequisite step not completed.`,
          rule_name: rule.rule_name
        };
      }
    }
    
    return {
      passed: true,
      severity: 'info',
      message: rule.validation_message.success || 'Sequence validation passed'
    };
  }
  
  /**
   * Validate completeness
   */
  validateCompleteness(rule, actionData) {
    const requiredEvidenceTypes = rule.rule_definition.required_evidence_types || [];
    const requiredFields = rule.rule_definition.required_fields || [];
    
    // Check evidence
    for (const evidenceType of requiredEvidenceTypes) {
      const hasEvidence = actionData.photo_urls?.length > 0 || 
                          actionData.signature_url || 
                          false;
      
      if (!hasEvidence) {
        return {
          passed: false,
          severity: rule.severity || 'warning',
          message: `Required ${evidenceType} evidence not captured.`,
          rule_name: rule.rule_name
        };
      }
    }
    
    // Check required fields
    for (const field of requiredFields) {
      if (!actionData[field] || actionData[field] === '') {
        return {
          passed: false,
          severity: rule.severity || 'warning',
          message: `Required field '${field}' is missing.`,
          rule_name: rule.rule_name
        };
      }
    }
    
    return {
      passed: true,
      severity: 'info',
      message: rule.validation_message.success || 'Completeness validation passed'
    };
  }
  
  /**
   * Validate quality
   */
  validateQuality(rule, actionData) {
    // Photo quality validation (placeholder - would need image analysis)
    const photoMinResolution = rule.rule_definition.photo_min_resolution;
    const photoMaxSize = rule.rule_definition.photo_max_size_kb;
    
    // For now, just check if photo exists
    if (photoMinResolution && actionData.photo_urls?.length === 0) {
      return {
        passed: false,
        severity: rule.severity || 'warning',
        message: `Photo evidence does not meet quality requirements.`,
        rule_name: rule.rule_name
      };
    }
    
    return {
      passed: true,
      severity: 'info',
      message: rule.validation_message.success || 'Quality validation passed'
    };
  }
  
  /**
   * Get validation rules for action
   */
  async getValidationRules(protocolId, actionId, stepId) {
    try {
      // Query rules that apply to this action
      const rules = [];
      
      // Rules for specific action
      if (actionId) {
        const actionRulesQuery = query(
          this.rulesCollection,
          where('applies_to.action_ids', 'array-contains', actionId)
        );
        const actionRulesSnapshot = await getDocs(actionRulesQuery);
        actionRulesSnapshot.forEach(doc => {
          rules.push({ id: doc.id, ...doc.data() });
        });
      }
      
      // Rules for step
      if (stepId) {
        const stepRulesQuery = query(
          this.rulesCollection,
          where('applies_to.step_ids', 'array-contains', stepId)
        );
        const stepRulesSnapshot = await getDocs(stepRulesQuery);
        stepRulesSnapshot.forEach(doc => {
          rules.push({ id: doc.id, ...doc.data() });
        });
      }
      
      // Rules for protocol
      if (protocolId) {
        const protocolRulesQuery = query(
          this.rulesCollection,
          where('applies_to.protocol_ids', 'array-contains', protocolId)
        );
        const protocolRulesSnapshot = await getDocs(protocolRulesQuery);
        protocolRulesSnapshot.forEach(doc => {
          rules.push({ id: doc.id, ...doc.data() });
        });
      }
      
      return rules;
    } catch (error) {
      console.error('Error getting validation rules:', error);
      return [];
    }
  }
}

/**
 * Validation Error
 */
export class ValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ValidationError';
  }
}

export const executionValidationService = new ExecutionValidationService();



